/**
 * "Best Price Promo" one-pager — the highest price promo of every project in the
 * Price Promo Units dialog, laid out on an A4 sheet over the Huttons backdrop.
 *
 * Pipeline: jsPDF draws the sheet as vector text over the background photo, then
 * pdf.js rasterises each page to a JPEG so the agent can drop the image straight
 * into WhatsApp. The PDF stays the source of truth (crisp text, exact layout);
 * the JPEG is what ships to the user.
 */
import { jsPDF, GState } from 'jspdf';
import { pdfjs } from 'react-pdf';
import BestPricePromoBg from '../../../img/best_price_promo_bg.jpg';
import HuttonsBlueLogo from '../../../img/Huttons_blue.png';
import HuttonsWhiteLogo from '../../../img/Huttons_white.png';

// pdf.js worker — same pinned CDN build the rest of the app uses (see
// super-picker/components/siteplan/TopViewingSitePlanDetail.tsx). Set
// unconditionally: react-pdf defaults it to a bare `pdf.worker.mjs` that this
// app does not serve, so a truthy check would leave the broken default in place.
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Images the sheet draws. jsPDF accepts either a loaded <img> (browser) or a
 * data URL string, so the drawing pass stays synchronous and side-effect free.
 */
export interface BestPricePromoAssets {
  background: HTMLImageElement | string;
  /** White wordmark for the footer band. */
  logo?: HTMLImageElement | string | null;
  /** Navy wordmark, tiled across the page as a faint watermark. */
  watermark?: HTMLImageElement | string | null;
}

/** Huttons_white.png is 4210x1035 — kept as a constant so drawing needs no DOM. */
const LOGO_ASPECT = 4210 / 1035;

/** One project's best promo, already reduced from its units. */
export interface BestPricePromoRow {
  projectName: string;
  /** Positive savings amount in SGD — the project's deepest promo. */
  saving: number;
}

export interface BestPricePromoOptions {
  rows: BestPricePromoRow[];
  /** `Name: … · CEA Registration Number: … · Contact Number: …` */
  agentLine: string;
  /** `Huttons Asia Pte Ltd · L3008899K` */
  agencyLine: string;
  /** Short note describing the active filters, e.g. `3BR · PH only`. */
  filterNote?: string;
}

// ── Palette ─────────────────────────────────────────────────────────────────
const NAVY: [number, number, number] = [15, 42, 69];
const NAVY_SOFT: [number, number, number] = [86, 104, 126];
const GOLD: [number, number, number] = [201, 164, 76];
const GREEN: [number, number, number] = [14, 118, 60];
const CARD_BORDER: [number, number, number] = [206, 215, 226];
const ROW_TINT: [number, number, number] = [240, 245, 250];
const BADGE_BG: [number, number, number] = [230, 236, 243];
const TNC_TEXT: [number, number, number] = [82, 98, 118];

// ── Page geometry (mm, A4 portrait) ─────────────────────────────────────────
const PAGE_W = 210;
const PAGE_H = 297;
const CARD_X = 14;
const CARD_W = PAGE_W - CARD_X * 2;
const CARD_TOP = 50;
const TABLE_X = CARD_X + 4;
const TABLE_W = CARD_W - 8;
const HEADER_H = 9;
const ROWS_TOP = CARD_TOP + 4 + HEADER_H + 3;
const CARD_BOTTOM_LIMIT = 268;
const COL_GAP = 8;
const FOOTER_BAND_Y = 279;
const FOOTER_BAND_H = PAGE_H - FOOTER_BAND_Y;

/** Row heights: the minimum that still reads well, and the most we will stretch to. */
const MIN_ROW_H = 8.4;
const MAX_SINGLE_ROW_H = 13.5;
const MAX_DOUBLE_ROW_H = 11.5;
/** Above this many projects the sheet switches to two columns. */
const SINGLE_COLUMN_MAX = 20;

const ROWS_SPACE = CARD_BOTTOM_LIMIT - ROWS_TOP;
const MAX_SINGLE_ROWS = Math.floor(ROWS_SPACE / MIN_ROW_H);
const MAX_DOUBLE_ROWS = MAX_SINGLE_ROWS * 2;

interface RowMetrics {
  amount: number;
  upTo: number;
  nameBase: number;
  nameMin: number;
  badgeR: number;
  /** Two-column rows drop the per-row "up to" — the column header carries it. */
  showUpTo: boolean;
}

/**
 * Type sizes for one page, scaled to the row height so a short list reads big
 * and a full two-column sheet still fits.
 */
function rowMetrics(rowH: number, compact: boolean): RowMetrics {
  const amount = Math.min(compact ? 13 : 15.5, rowH * (compact ? 1.22 : 1.42));
  return {
    amount,
    upTo: amount * 0.6,
    nameBase: amount * 0.84,
    nameMin: amount * 0.58,
    badgeR: Math.min(3.4, rowH * 0.32),
    showUpTo: !compact,
  };
}

/**
 * Savings the way the per-unit promo designs write them — `$205K`, `$1.7M`.
 * Shared with the price-chart Share tab so both surfaces read the same.
 */
export function formatSavingCompact(saving: number): string {
  const value = Math.abs(saving);
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (value >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${Math.round(value)}`;
}

/**
 * Savings in thousands throughout — `$205K`, `$1,700K` — so a column of figures
 * compares at a glance instead of mixing K and M scales.
 */
export function formatSavingThousands(saving: number): string {
  const value = Math.abs(saving);
  if (value < 1000) return `$${Math.round(value)}`;
  return `$${Math.round(value / 1000).toLocaleString('en-SG')}K`;
}

const sheetDate = (): string =>
  new Date().toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });

/** Load a bundled asset as an <img> so jsPDF can embed it. */
const loadImageEl = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });

/**
 * Draw letterspaced text with real centre / right alignment.
 *
 * jsPDF measures the string *without* `charSpace` when it aligns, so a spaced
 * heading drifts right by the whole tracking amount. Measure it ourselves and
 * always draw from the left.
 */
function drawSpacedText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  charSpace: number,
  align: 'left' | 'center' | 'right' = 'left',
): void {
  const width = doc.getTextWidth(text) + charSpace * Math.max(0, text.length - 1);
  const left = align === 'center' ? x - width / 2 : align === 'right' ? x - width : x;
  doc.text(text, left, y, { charSpace });
}

/** Trim `text` with an ellipsis until it fits `maxWidth` at the current font. */
function clipText(doc: jsPDF, text: string, maxWidth: number): string {
  if (doc.getTextWidth(text) <= maxWidth) return text;
  let clipped = text;
  while (clipped.length > 1 && doc.getTextWidth(`${clipped}…`) > maxWidth) {
    clipped = clipped.slice(0, -1);
  }
  return `${clipped}…`;
}

/**
 * Horizontal budget for one row: the savings block is measured first, and the
 * project name gets whatever is left of the column.
 */
function rowSpace(doc: jsPDF, row: BestPricePromoRow, width: number, metrics: RowMetrics) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(metrics.amount);
  const amountW = doc.getTextWidth(formatSavingThousands(row.saving));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(metrics.upTo);
  const upToW = metrics.showUpTo ? doc.getTextWidth('up to ') : 0;
  const nameX = 2.6 + metrics.badgeR * 2 + 2.8;
  return { amountW, upToW, nameX, nameMaxW: width - 2.6 - amountW - upToW - 3 - nameX };
}

/**
 * One name size for the whole sheet — the largest at which every project fits
 * its row, so the table reads evenly instead of each row picking its own size.
 * A name that still does not fit at the floor size is ellipsised when drawn.
 */
function uniformNameSize(
  doc: jsPDF,
  rows: BestPricePromoRow[],
  width: number,
  metrics: RowMetrics,
): number {
  const budgets = rows.map((row) => ({
    name: row.projectName.toUpperCase(),
    maxW: rowSpace(doc, row, width, metrics).nameMaxW,
  }));
  doc.setFont('helvetica', 'bold');
  for (let size = metrics.nameBase; size > metrics.nameMin; size -= 0.2) {
    doc.setFontSize(size);
    if (budgets.every((b) => doc.getTextWidth(b.name) <= b.maxW)) return size;
  }
  return metrics.nameMin;
}

/** Split the rows into pages, balancing the last page instead of leaving 1 orphan. */
function paginate(total: number): { pages: number[][]; twoColumns: boolean } {
  const twoColumns = total > SINGLE_COLUMN_MAX;
  const perPageMax = twoColumns ? MAX_DOUBLE_ROWS : MAX_SINGLE_ROWS;
  const pageCount = Math.max(1, Math.ceil(total / perPageMax));
  const perPage = Math.ceil(total / pageCount);

  const pages: number[][] = [];
  for (let start = 0; start < total; start += perPage) {
    const page: number[] = [];
    for (let i = start; i < Math.min(start + perPage, total); i += 1) page.push(i);
    pages.push(page);
  }
  return { pages: pages.length ? pages : [[]], twoColumns };
}

// ── Watermark ───────────────────────────────────────────────────────────────
/** Barely-there tiling of the wordmark, so a forwarded screenshot stays ours. */
const WM_OPACITY = 0.04;
const WM_TILE_W = 44;
const WM_STEP_X = 62;
const WM_STEP_Y = 40;
const WM_ANGLE = 24;

/** Tile the navy wordmark diagonally across the whole page. */
function drawWatermark(doc: jsPDF, watermark: HTMLImageElement | string): void {
  const tileH = WM_TILE_W / LOGO_ASPECT;
  doc.setGState(new GState({ opacity: WM_OPACITY }));
  let row = 0;
  for (let y = -WM_STEP_Y; y < PAGE_H + WM_STEP_Y; y += WM_STEP_Y, row += 1) {
    const stagger = row % 2 === 0 ? 0 : WM_STEP_X / 2;
    for (let x = -WM_TILE_W + stagger; x < PAGE_W + WM_TILE_W; x += WM_STEP_X) {
      // One alias so the PNG is embedded once and merely re-referenced per tile.
      doc.addImage(watermark, 'PNG', x, y, WM_TILE_W, tileH, 'huttons-watermark', 'FAST', WM_ANGLE);
    }
  }
  doc.setGState(new GState({ opacity: 1 }));
}

/** Rank badge + project name + "up to $X", drawn inside one column cell. */
function drawRow(
  doc: jsPDF,
  row: BestPricePromoRow,
  rank: number,
  x: number,
  y: number,
  width: number,
  rowH: number,
  metrics: RowMetrics,
  nameSize: number,
): void {
  const midY = y + rowH / 2;

  if (rank % 2 === 1) {
    doc.setFillColor(...ROW_TINT);
    doc.roundedRect(x, y, width, rowH, 1.4, 1.4, 'F');
  }

  // Rank badge — gold for the top three savings on the sheet.
  const { badgeR } = metrics;
  const badgeX = x + 2.6 + badgeR;
  const isTop = rank <= 3;
  doc.setFillColor(...(isTop ? GOLD : BADGE_BG));
  doc.circle(badgeX, midY, badgeR, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(badgeR * 2.2);
  doc.setTextColor(...(isTop ? [42, 30, 4] as [number, number, number] : NAVY));
  doc.text(String(rank), badgeX, midY + badgeR * 0.28, { align: 'center' });

  // Savings, right-aligned: small grey "up to" + bold green amount.
  const space = rowSpace(doc, row, width, metrics);
  const amountRight = x + width - 2.6;
  const baseline = midY + metrics.amount * 0.115;
  if (metrics.showUpTo) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(metrics.upTo);
    doc.setTextColor(...NAVY_SOFT);
    doc.text('up to', amountRight - space.amountW - space.upToW, baseline);
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(metrics.amount);
  doc.setTextColor(...GREEN);
  doc.text(formatSavingThousands(row.saving), amountRight, baseline, { align: 'right' });

  // Project name — one size across the sheet, ellipsised only if it must be.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(nameSize);
  doc.setTextColor(...NAVY);
  doc.text(clipText(doc, row.projectName.toUpperCase(), space.nameMaxW), x + space.nameX, baseline);
}

/**
 * Draw the whole A4 sheet — one page per chunk of rows, each page complete with
 * title and agent footer so any single page works on its own as a share image.
 */
export function drawBestPricePromoSheet(
  options: BestPricePromoOptions,
  assets: BestPricePromoAssets,
): jsPDF {
  const { rows, agentLine, agencyLine, filterNote } = options;
  const bg = assets.background;
  const logo = assets.logo;
  const watermark = assets.watermark;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const { pages, twoColumns } = paginate(rows.length);
  const asAt = sheetDate();

  // Stretch the rows towards the footer so a short list is not marooned at the
  // top of the page; the tallest page decides, so every page shares a rhythm.
  const tallestColumn = Math.max(
    ...pages.map((page) => (twoColumns ? Math.ceil(page.length / 2) : page.length)),
  );
  const rowH = Math.max(
    MIN_ROW_H,
    Math.min(twoColumns ? MAX_DOUBLE_ROW_H : MAX_SINGLE_ROW_H, ROWS_SPACE / Math.max(1, tallestColumn)),
  );
  const metrics = rowMetrics(rowH, twoColumns);

  pages.forEach((pageRows, pageIndex) => {
    if (pageIndex > 0) doc.addPage();

    doc.addImage(bg, 'JPEG', 0, 0, PAGE_W, PAGE_H);

    // ── Header ──
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.6);
    doc.setTextColor(...NAVY_SOFT);
    drawSpacedText(doc, 'SUPER PICKER', PAGE_W / 2, 21, 1.4, 'center');

    doc.setFontSize(30);
    doc.setTextColor(...NAVY);
    drawSpacedText(doc, 'HUGE SAVINGS', PAGE_W / 2, 34, 1.2, 'center');

    doc.setFillColor(...GOLD);
    doc.rect(PAGE_W / 2 - 15, 37.5, 30, 1.1, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.8);
    doc.setTextColor(...NAVY_SOFT);
    const subtitle = [
      `Massive discounts by developer · ${rows.length} project${rows.length === 1 ? '' : 's'}`,
      filterNote,
      `as at ${asAt}`,
    ]
      .filter(Boolean)
      .join('  ·  ');
    doc.text(subtitle, PAGE_W / 2, 44.5, { align: 'center' });

    // ── Card ──
    const columnRowCount = twoColumns ? Math.ceil(pageRows.length / 2) : pageRows.length;
    const cardH = 4 + HEADER_H + 3 + columnRowCount * rowH + 4;
    doc.setGState(new GState({ opacity: 0.9 }));
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(CARD_X, CARD_TOP, CARD_W, cardH, 3.5, 3.5, 'F');
    doc.setGState(new GState({ opacity: 1 }));
    doc.setDrawColor(...CARD_BORDER);
    doc.setLineWidth(0.3);
    doc.roundedRect(CARD_X, CARD_TOP, CARD_W, cardH, 3.5, 3.5, 'S');

    // Column header pill
    doc.setFillColor(...NAVY);
    doc.roundedRect(TABLE_X, CARD_TOP + 4, TABLE_W, HEADER_H, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.4);
    doc.setTextColor(255, 255, 255);
    const headerMid = CARD_TOP + 4 + HEADER_H / 2 + 1;
    if (twoColumns) {
      const colW = (TABLE_W - COL_GAP) / 2;
      [0, 1].forEach((col) => {
        const x = TABLE_X + col * (colW + COL_GAP);
        drawSpacedText(doc, 'PROJECT', x + 3, headerMid, 0.8);
        drawSpacedText(doc, 'SAVINGS UP TO', x + colW - 3, headerMid, 0.8, 'right');
      });
    } else {
      drawSpacedText(doc, 'PROJECT', TABLE_X + 4, headerMid, 1);
      drawSpacedText(doc, 'SAVINGS UP TO', TABLE_X + TABLE_W - 4, headerMid, 1, 'right');
    }

    // ── Rows ──
    const columnW = twoColumns ? (TABLE_W - COL_GAP) / 2 : TABLE_W;
    const nameSize = uniformNameSize(doc, pageRows.map((i) => rows[i]), columnW, metrics);

    if (twoColumns) {
      const colW = (TABLE_W - COL_GAP) / 2;
      pageRows.forEach((rowIndex, i) => {
        const col = i < columnRowCount ? 0 : 1;
        const slot = i < columnRowCount ? i : i - columnRowCount;
        drawRow(
          doc,
          rows[rowIndex],
          rowIndex + 1,
          TABLE_X + col * (colW + COL_GAP),
          ROWS_TOP + slot * rowH,
          colW,
          rowH,
          metrics,
          nameSize,
        );
      });
    } else {
      pageRows.forEach((rowIndex, i) => {
        drawRow(doc, rows[rowIndex], rowIndex + 1, TABLE_X, ROWS_TOP + i * rowH, TABLE_W, rowH, metrics, nameSize);
      });
    }

    // Watermark last, so it lies faintly over the card as well as the photo —
    // the footer band is drawn after it and stays clean.
    if (watermark) drawWatermark(doc, watermark);

    // ── Footer ──
    // T&C rides on a soft white plate: legible over the photo at a quiet weight,
    // so it never has to go dark and heavy enough to fight the table above.
    const tnc = 'Image is for illustration purposes only  ·  Limited time offer  ·  * T&C applies.';
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.8);
    const tncW = doc.getTextWidth(tnc);
    const plateH = 7.4;
    const plateY = 269;
    doc.setGState(new GState({ opacity: 0.68 }));
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(PAGE_W / 2 - tncW / 2 - 7, plateY, tncW + 14, plateH, plateH / 2, plateH / 2, 'F');
    doc.setGState(new GState({ opacity: 1 }));
    doc.setTextColor(...TNC_TEXT);
    doc.text(tnc, PAGE_W / 2, plateY + plateH / 2 + 1.15, { align: 'center' });

    if (pages.length > 1) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...NAVY_SOFT);
      doc.text(`${pageIndex + 1} / ${pages.length}`, PAGE_W - CARD_X, plateY + plateH / 2 + 1, { align: 'right' });
    }

    doc.setGState(new GState({ opacity: 0.94 }));
    doc.setFillColor(...NAVY);
    doc.rect(0, FOOTER_BAND_Y, PAGE_W, FOOTER_BAND_H, 'F');
    doc.setGState(new GState({ opacity: 1 }));

    if (logo) {
      const logoH = 4.6;
      doc.addImage(logo, 'PNG', CARD_X - 2, FOOTER_BAND_Y + (FOOTER_BAND_H - logoH) / 2, logoH * LOGO_ASPECT, logoH);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.2);
    doc.setTextColor(255, 255, 255);
    doc.text(agentLine, PAGE_W / 2, FOOTER_BAND_Y + 7.4, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    doc.setTextColor(226, 232, 240);
    doc.text(agencyLine, PAGE_W / 2, FOOTER_BAND_Y + 12.6, { align: 'center' });
  });

  return doc;
}

/** Load the bundled artwork, then draw the sheet. */
export async function buildBestPricePromoPdf(options: BestPricePromoOptions): Promise<jsPDF> {
  const [background, logo, watermark] = await Promise.all([
    loadImageEl(BestPricePromoBg),
    loadImageEl(HuttonsWhiteLogo).catch(() => null),
    loadImageEl(HuttonsBlueLogo).catch(() => null),
  ]);
  return drawBestPricePromoSheet(options, { background, logo, watermark });
}

/**
 * Rasterise every page of `doc` to a JPEG via pdf.js.
 *
 * `targetWidth` is the pixel width of the rendered page — 1654 px across A4 is
 * ~200 DPI, sharp on a phone screen without producing a 10 MB image.
 */
export async function renderPdfToJpegBlobs(doc: jsPDF, targetWidth = 1654): Promise<Blob[]> {
  const data = doc.output('arraybuffer');
  const pdf = await pdfjs.getDocument({ data: new Uint8Array(data) }).promise;
  const blobs: Blob[] = [];

  try {
    for (let pageNo = 1; pageNo <= pdf.numPages; pageNo += 1) {
      const page = await pdf.getPage(pageNo);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: targetWidth / base.width });

      const canvas = document.createElement('canvas');
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);

      // Hand pdf.js the canvas (not a context) — the documented path in pdf.js 5;
      // the sheet's photo covers the page, but white backs any anti-aliased edge.
      await page.render({ canvas, viewport, background: '#ffffff' }).promise;
      page.cleanup();

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.92),
      );
      // Release the backing store before decoding the next page (iOS Safari).
      canvas.width = 0;
      canvas.height = 0;
      if (!blob) throw new Error('canvas.toBlob returned null');
      blobs.push(blob);
    }
  } finally {
    await pdf.destroy();
  }

  return blobs;
}

/**
 * Reduce the dialog's promo units to one row per project — the deepest discount
 * that project offers — sorted biggest saving first.
 */
export function toBestPricePromoRows(
  units: Array<{ projectName: string; ProjectId: string; priceChange: number }>,
): BestPricePromoRow[] {
  const byProject = new Map<string, BestPricePromoRow>();

  for (const unit of units) {
    const saving = Math.abs(Number(unit.priceChange) || 0);
    if (saving <= 0) continue;
    const key = String(unit.ProjectId ?? '').trim() || unit.projectName;
    const existing = byProject.get(key);
    if (!existing) {
      byProject.set(key, { projectName: unit.projectName, saving });
    } else if (saving > existing.saving) {
      existing.saving = saving;
    }
  }

  return Array.from(byProject.values()).sort(
    (a, b) => b.saving - a.saving || a.projectName.localeCompare(b.projectName),
  );
}
